import React, { useEffect, useState } from 'react'

import { SegmentAction, segmentTracking } from 'src/helpers/segment'

import './ProposalLoadingInterstitial.scss'
import {
  ProposalLoadingStage,
  ProposalLoadingStageConfig,
  getProposalLoadingStageConfig,
  useDisplayedProposalLoadingStage,
} from './proposalLoadingStages'

interface ProposalLoadingInterstitialProps {
  stage?: ProposalLoadingStage
}

// Once the stage's own fill has landed, ease on toward creepToPercent so a stage
// the backend holds for many seconds still shows movement.
const useCreepingFill = ({
  stage,
  progressPercent,
  fillDurationMs,
  creepToPercent,
  creepDurationMs,
}: ProposalLoadingStageConfig) => {
  const [creeping, setCreeping] = useState(false)

  useEffect(() => {
    setCreeping(false)
    const timer = window.setTimeout(() => setCreeping(true), fillDurationMs)

    return () => window.clearTimeout(timer)
  }, [stage, fillDurationMs])

  return creeping
    ? { fillPercent: creepToPercent, fillDuration: creepDurationMs }
    : { fillPercent: progressPercent, fillDuration: fillDurationMs }
}

export const ProposalLoadingInterstitial = ({ stage }: ProposalLoadingInterstitialProps) => {
  const displayedStage = useDisplayedProposalLoadingStage(stage)

  useEffect(() => {
    const shownAt = Date.now()
    segmentTracking(SegmentAction.SQ_PROPOSAL_LOADING_SHOWN)

    return () => {
      // Unmount runs on the navigation to results, so a throwing analytics client must not reach React.
      try {
        segmentTracking(SegmentAction.SQ_PROPOSAL_LOADING_DISMISSED, { elapsedMs: Date.now() - shownAt })
      } catch {
        return
      }
    }
  }, [])

  const config = getProposalLoadingStageConfig(displayedStage)
  const { fillPercent, fillDuration } = useCreepingFill(config)

  return (
    <div className="loader_full_screen proposal-loading-interstitial">
      <div className="proposal-loading-inner">
        <h2 className="proposal-loading-headline">{config.headline}</h2>
        <div
          className="proposal-loading-progress"
          role="progressbar"
          aria-label="Building your proposal"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={fillPercent}
        >
          <div
            className="proposal-loading-progress-fill"
            data-testid="proposal-loading-progress-fill"
            style={
              {
                width: `${fillPercent}%`,
                '--proposal-loading-fill-duration': `${fillDuration}ms`,
              } as React.CSSProperties
            }
          />
        </div>
      </div>
    </div>
  )
}
